import React, { HTMLInputTypeAttribute, ReactNode } from 'react'
import clsx from 'clsx'
import { Field, FieldHookConfig, useField, useFormikContext } from 'formik'
import { SizeVariants } from '@/shared/forms/components/constants'
import Markdown from 'react-markdown'
import { SimpleTooltip } from '@/shared/components/common/Tooltip'
import Link from 'next/link'

export type FormikInputProps = {
  label: string
  size: keyof typeof SizeVariants
  autocomplete: string
  disabled?: boolean
  placeholder?: string
  optional?: boolean
  type?: HTMLInputTypeAttribute
  tooltip?: ReactNode
} & FieldHookConfig<string>

export default function FormikToggle({
  label,
  size,
  autocomplete,
  disabled,
  placeholder,
  tooltip,

  optional = false,
  ...props
}: FormikInputProps) {
  const [_field, meta] = useField(props)
  const { isSubmitting } = useFormikContext()
  const isDisabled = disabled || isSubmitting

  return (
    <div className={clsx(SizeVariants[size as keyof typeof SizeVariants], props.className)}>
      <div className="flex items-center justify-start gap-x-3">
        <Field
          type="checkbox"
          name={props.name}
          className={clsx(
            'h-4 w-4 border-gray-300 focus:ring-blue-600',
            isDisabled ? 'text-slate-300' : 'text-blue-600',
          )}
          disabled={isDisabled}
        />

        <label
          htmlFor={props.name}
          className={clsx(
            'block text-sm font-medium leading-6',
            isDisabled ? 'text-gray-500' : 'text-gray-900',
          )}
        >
          <Markdown
            components={{
              a: (link) => (
                <Link
                  className="text-blue-600 hover:underline"
                  href={link.href!}
                  target="_blank"
                >
                  {link.children}
                </Link>
              ),
            }}
          >
            {label}
          </Markdown>
        </label>
        {tooltip && <SimpleTooltip content={tooltip} disabled={disabled} />}
        {optional && (
          <span className="text-sm leading-6 text-gray-500" id="email-optional">
            Optional
          </span>
        )}
      </div>
      {meta.touched && meta.error && (
        <div className="mt-2 text-sm font-semibold text-red-600">
          {meta.error}
        </div>
      )}
    </div>
  )
}
